import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { v4 as uuidv4 } from "uuid";
import sampleFile from "./sample_qc_upload.csv";
import sampleErrorFile from "./sample_qc_upload_error.csv";
import { displayMessage } from "../../Utils/helper";

const { REACT_APP_API_BASE_URL } = process.env;
const fresh_upload_id = uuidv4();

/* ══════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════ */

const ISSUE_META = {
  NULL_VALUE:          { label: "Missing Values",     color: "#ef4444", bg: "#fef2f2", icon: "⊘" },
  NEGATIVE_SALES:      { label: "Negative Sales",     color: "#dc2626", bg: "#fef2f2", icon: "↓" },
  FRACTIONAL_SALES:    { label: "Fractional Sales",   color: "#E06639", bg: "#fff7ed", icon: "½" },
  ZERO_SALES:          { label: "Zero Sales",         color: "#eab308", bg: "#fefce8", icon: "0" },
  OUTLIER:             { label: "Outliers",           color: "#E06639", bg: "#f5f3ff", icon: "⚡" },
  FLAT_LINING:         { label: "Flat-lining",        color: "#6a7d8c", bg: "#f4f3ef", icon: "—" },
  ZERO_ASP:            { label: "Zero ASP",           color: "#E06639", bg: "#fff7ed", icon: "!" },
  FLAT_LINE:           { label: "Flat-lining",        color: "#6a7d8c", bg: "#f4f3ef", icon: "—" },
  MISSING_TIMESERIES:  { label: "Missing Time-series", color: "#06b6d4", bg: "#ecfeff", icon: "◷" },
  DUPLICATE_ROWS:      { label: "Duplicate Rows",     color: "#e11d48", bg: "#fff1f2", icon: "⚠" },
};

const SEVERITY_META = {
  ERROR: { label: "Error",   color: "#dc2626", bg: "#fef2f2" },
  WARN:  { label: "Warning", color: "#E06639", bg: "#fffbeb" },
  INFO:  { label: "Info",    color: "#032B4E", bg: "#eff6ff" },
};

const validateField = (key, value) => {
  const stringFields = ["sku_level1","sku_level2","sku_level3","sku_level4","sku_level5","sku_level6","level1","level2","level3","level4","level5"];
  if (stringFields.includes(key)) return typeof value === "string" ? value : String(value || "");
  if (key === "sales") {
    const num = Number(value);
    return Number.isNaN(num) ? 0 : Math.round(num);
  }
  return value;
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */

export default function QCDashboard({ workflow_name, description }) {
  const [file,       setFile]       = useState(null);
  const [initialResult, setInitialResult] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [result,     setResult]     = useState(null);
  const [activeTab,  setActiveTab]  = useState(null);
  const [fixes,      setFixes]      = useState({});
  const [saved,      setSaved]      = useState({});
  const [saving,     setSaving]     = useState(null);
  const [committing, setCommitting] = useState(false);

  const handleUpload = async (fileOverride) => {
    const uploadFile = fileOverride instanceof File ? fileOverride : file;
    if (!uploadFile) return displayMessage("warning", "Missing File", "Please select a file first");
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("data_upload_id", fresh_upload_id);
    fd.append("user_id", "a8a4e241-883d-4841-ab1d-437106654754");
    fd.append("frequency", "monthly");
    try {
      setLoading(true);
      if (fileOverride instanceof File) setFile(uploadFile);
      setResult(null);
      setFixes({});
      setSaved({});
      const res = await axios.post(`${REACT_APP_API_BASE_URL}/qc/upload`, fd);
      setInitialResult(res.data);
      setResult(res.data);
      const types = [...new Set((res.data.issues || []).map(i => i.type))];
      if (types.length) setActiveTab(types[0]);
    } catch { displayMessage("danger", "Upload Failed", "Upload & QC failed. Please try again."); }
    finally { setLoading(false); }
  };

  // Fetch the bundled "dirty" sample file and run it straight through validation
  const handleTrySampleError = async () => {
    try {
      setLoadingSample(true);
      const res = await fetch(sampleErrorFile);
      const blob = await res.blob();
      const sampleErrorFileObj = new File([blob], "sample_qc_upload_error.csv", { type: "text/csv" });
      await handleUpload(sampleErrorFileObj);
    } catch {
      displayMessage("danger", "Load Failed", "Could not load the sample error file.");
    } finally {
      setLoadingSample(false);
    }
  };

  // Clear saved state for a row so it can be re-edited. Keyed by row+column
  // only (no issue type) so "un-saving" is consistent no matter which tab
  // you're viewing the row from.
  const handleUnsaveRow = (rowNum, rowIssues) => {
    setSaved(prev => {
      const updated = { ...prev };
      rowIssues.forEach(issue => {
        delete updated[`${rowNum}-${issue.col}`];
      });
      return updated;
    });
  };

  // Save a row's fixes with a single API call.
  // `ownIssues` = this tab's own issue(s) for the row — always marked resolved
  // on Save, exactly like before (clicking Save on a Fractional Sales row
  // resolves that Fractional Sales issue regardless of the value entered).
  // `bonusIssues` = issues of OTHER types on the same physical row (e.g. a
  // null Range while viewing Fractional Sales) — only resolved if the user
  // actually typed a fix for them, so an untouched unrelated problem doesn't
  // get silently marked done just because it shares a row with this save.
  const handleSaveRowBatch = async (rowNum, ownIssues, bonusIssues = []) => {
    const baseRowData = ownIssues[0].row_data || {};

    const editedBonusIssues = bonusIssues.filter(issue => fixes[`${rowNum}-${issue.col}`] !== undefined);
    const affectedIssues = [...ownIssues, ...editedBonusIssues];

    // Collect user-entered patches across all affected columns in this row
    const allPatches = {};
    affectedIssues.forEach(issue => {
      const patch = fixes[`${rowNum}-${issue.col}`] || {};
      Object.assign(allPatches, patch);
    });

    // Build full validated payload: start from complete row_data, apply patches on top
    const validated = {};
    Object.keys({ ...baseRowData, ...allPatches }).forEach(key => {
      const val = allPatches[key] !== undefined ? allPatches[key] : baseRowData[key];
      validated[key] = validateField(key, val);
    });

    const payload = {
      data_upload_id: fresh_upload_id,
      fixes: [{ row: ownIssues[0].row, values: validated }],
    };

    try {
      setSaving(rowNum);
      await axios.patch(`${REACT_APP_API_BASE_URL}/qc/fix`, payload);

      setSaved(prev => {
        const updated = { ...prev };
        affectedIssues.forEach(issue => {
          updated[`${rowNum}-${issue.col}`] = true;
        });
        return updated;
      });

      // Update row_data with the validated values but keep the row visible in the table
      setResult(prev => ({
        ...prev,
        issues: prev.issues.map(i =>
          i.row === ownIssues[0].row && affectedIssues.some(ri => ri.col === i.col && ri.type === i.type)
            ? { ...i, row_data: { ...i.row_data, ...validated } }
            : i
        ),
        issue_count: Math.max(0, (prev.issue_count || 0) - affectedIssues.length),
      }));
    } catch {
      displayMessage("danger", "Save Failed", "Failed to save fix.");
    } finally {
      setSaving(null);
    }
  };

  const downloadExcel = async () => {
    if (!file) return displayMessage("warning", "File Missing", "Original file not found. Please re-upload.");
    const fixableIssues = (result?.issues || []).filter(i => i.row_data);
    if (!fixableIssues.length) return displayMessage("warning", "Nothing to Download", "No rows to download");

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      const range = XLSX.utils.decode_range(ws['!ref']);

      // Extract headers to match row_data accurately
      const headers = [];
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell = ws[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
        headers[C] = cell ? cell.v : null;
      }

      const rowsToHighlight = new Set();

      // Map backend row numbers accurately to the original file
      fixableIssues.forEach(iss => {
        const rBase = Number(iss.row);
        let found = false;
        for (let offset of [-1, 0, 1, 2]) {
          const R = rBase + offset;
          if (R <= range.s.r || R > range.e.r) continue;

          let match = true;
          for (let C = range.s.c; C <= range.e.c; C++) {
            if (!headers[C]) continue;
            const key = headers[C];
            if (iss.row_data[key] !== undefined) {
              const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
              const xlVal = cell ? cell.v : null;
              const issVal = iss.row_data[key];
              if (issVal != null && xlVal != null && String(issVal).trim() !== String(xlVal).trim()) {
                match = false;
                break;
              }
            }
          }
          if (match) {
            rowsToHighlight.add(R);
            found = true;
            break;
          }
        }
        if (!found) {
          // Fallback assumption: iss.row is the 1-based Excel row
          rowsToHighlight.add(rBase - 1);
        }
      });

      // Apply red fill ONLY to identified issue rows
      for (let R = range.s.r + 1; R <= range.e.r; R++) {
        if (rowsToHighlight.has(R)) {
          for (let C = range.s.c; C <= range.e.c; C++) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            if (!ws[addr]) ws[addr] = { v: "", t: "s" };
            ws[addr].s = {
              fill: { patternType: "solid", fgColor: { rgb: "FFCCCB" } },
              font: { color: { rgb: "8B0000" }, bold: true },
            };
          }
        }
      }

      XLSX.writeFile(wb, `QC_Issues_${fresh_upload_id}.xlsx`);
    } catch (error) {
      console.error("Error generating Excel:", error);
      displayMessage("danger", "Export Failed", "Failed to generate Excel file.");
    }
  };

  const handleCommit = async () => {
    try {
      setCommitting(true);
      console.log("Committing data with payload:", workflow_name,description);
      
      await axios.post(`${REACT_APP_API_BASE_URL}/qc/commit`, {
        data_upload_id: fresh_upload_id,
        user_id: "a8a4e241-883d-4841-ab1d-437106654754",
        strategy: "append",
        require_green: false,
        dataset_name: workflow_name,
        remark: description,
      });
      displayMessage("success", "Success", "Data committed successfully!");
    } catch { displayMessage("danger", "Commit Failed", "Commit failed. Fix all errors first."); }
    finally { setCommitting(false); }
  };

  const issues       = result?.issues || [];
  const summary      = result?.summary || {};
  
  const initialFixableCount = useMemo(() => {
    return (initialResult?.issues || []).filter(i => i.row_data && i.type !== "MISSING_TIMESERIES" && i.type !== "DUPLICATE_ROWS").length;
  }, [initialResult]);
  const baseFixableCount = useMemo(() => {
    return issues.filter(i => i.row_data && i.type !== "MISSING_TIMESERIES" && i.type !== "DUPLICATE_ROWS").length;
  }, [issues]);

  const savedCount = Object.keys(saved).length;
  const currentFixableCount = Math.max(0, baseFixableCount - savedCount);

  const solvedCount = initialFixableCount > 0 ? initialFixableCount - currentFixableCount : 0;
  
  const score = baseFixableCount > 0
      ? Math.round((solvedCount / baseFixableCount) * 100)
      : 100;

  // "Ready to commit" means zero fixable issues remain — not an arbitrary
  // score threshold, so the badge and commit button never disagree.
  const isGreen = currentFixableCount === 0;
  
  const issuesByType = useMemo(() => {
    const map = {};
    issues.forEach(iss => {
      if (!map[iss.type]) map[iss.type] = [];
      map[iss.type].push(iss);
    });
    return map;
  }, [issues]);

  // Every issue for a given row, across ALL issue types — not just the type
  // of the tab currently being viewed. A row can be null in one column
  // (flagged under Missing Values) while also being e.g. fractional sales
  // (flagged under Fractional Sales); this lets either tab's view of that
  // row offer a fix for both, and keeps "fixed" state shared between tabs.
  const issuesByRowAll = useMemo(() => {
    const map = {};
    issues.forEach(iss => {
      if (!iss.row_data) return;
      if (!map[iss.row]) map[iss.row] = [];
      map[iss.row].push(iss);
    });
    return map;
  }, [issues]);

  const tabTypes = useMemo(() => {
    return Object.keys(issuesByType).filter(type => {
      if (type === "MISSING_TIMESERIES" || type === "DUPLICATE_ROWS") return true;
      return issuesByType[type].some(i => i.row_data);
    });
  }, [issuesByType]);

  const activeIssues = useMemo(() => {
    if (!activeTab) return [];
    const activeList = issuesByType[activeTab] || [];
    if (activeTab === "MISSING_TIMESERIES" || activeTab === "DUPLICATE_ROWS") {
      return activeList;
    }
    return activeList.filter(i => i.row_data);
  }, [issuesByType, activeTab]);

  useEffect(() => {
    if (!tabTypes.length) return;
    if (!activeTab || !tabTypes.includes(activeTab)) {
      setActiveTab(tabTypes[0]);
    }
  }, [tabTypes, activeTab]);

  const displayIssueCount = useMemo(() => {
    if (typeof result?.issue_count === "number" && result.issue_count >= 0) {
      return result.issue_count;
    }
    return issues.length;
  }, [result, issues]);

  const hasTimeseries = summary.missing_timeseries_total > 0;
  const hasDuplicates = summary.duplicate_rows > 0;

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .btn-hover:hover { filter:brightness(0.97); }
        .qc-file-label:hover { border-color:#0c3c54; }
        @media (max-width: 640px) {
          .qc-upload-toolbar { flex-direction:column; align-items:stretch; }
          .qc-upload-samples { justify-content:center; }
          .qc-upload-action { flex-direction:column; align-items:stretch; }
          .qc-upload-action button, .qc-file-label { width:100%; box-sizing:border-box; }
          .qc-file-label span:last-child { max-width:none !important; flex:1; }
          .qc-stats-grid { grid-template-columns:repeat(2, 1fr) !important; }
          .qc-metrics-panel { padding:18px 16px !important; }
          .qc-header { padding:20px !important; }
          .qc-card { padding:16px !important; }
        }
        @media (max-width: 400px) {
          .qc-stats-grid { grid-template-columns:1fr !important; }
        }
        .row-fix-input:focus { outline:none; border-color:#E06639 !important; box-shadow:0 0 0 3px rgba(224,102,57,.18); }
        ::-webkit-scrollbar { height:6px; width:6px; }
        ::-webkit-scrollbar-track { background:#f6fafd; }
        ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }
        .tooltip { position:relative; cursor:help; }
        .tooltip:hover::after { content:attr(data-tip); position:absolute; bottom:100%; left:50%; transform:translateX(-50%);
          background:#032B4E; color:#fff; padding:6px 10px; border-radius:6px; font-size:11px; white-space:nowrap; z-index:10; }
        .qc-tab:hover { transform:translateY(-2px); box-shadow:0 4px 12px rgba(3,43,78,.1); }
        .qc-metric-card:hover { transform:translateY(-2px); box-shadow:0 6px 16px rgba(3,43,78,.08); border-color:#bcd4e6; }
      `}</style>

      <div className="qc-card" style={S.card}>

        {/* ══ HEADER ══ */}
        <div className="qc-header" style={S.header}>
          <div style={{ display:"flex", alignItems:"center", gap:"14px", flex:1, minWidth:"220px" }}>
            <div style={S.headerBadge}>🛡️</div>
            <div>
              <div style={S.eyebrow}>DATA QUALITY CONTROL SYSTEM</div>
              <h1 style={S.title}>Quality Check Dashboard</h1>
              <p style={S.subtitle}>
                Automated validation for product hierarchies, geography &amp; sales metrics
              </p>
            </div>
          </div>
          {result && <ScoreCard isGreen={isGreen} score={score} remaining={currentFixableCount} />}
        </div>

        {/* ══ UPLOAD ══ */}
        <div style={S.uploadZone}>
          <div style={S.uploadLeft}>
            <div style={S.uploadIcon}>📂</div>
            <div>
              <p style={S.uploadTitle}>Upload Data File</p>
              <p style={S.uploadSub}>Excel (.xlsx, .xls) or CSV · product, geography &amp; sales</p>
            </div>
          </div>

          <div className="qc-upload-toolbar" style={S.uploadToolbar}>
            <div className="qc-upload-samples" style={S.uploadSampleGroup}>
              <a
                href={sampleFile}
                download="sample_qc_upload.csv"
                style={S.secondaryBtn}
                className="btn-hover"
              >
                Download Sample
              </a>
              <button
                type="button"
                style={S.sampleErrorBtn}
                onClick={handleTrySampleError}
                disabled={loadingSample || loading}
                className="btn-hover"
                title="Load a sample file that already contains QC issues"
              >
                {loadingSample ? "Loading…" : "⚠ Try Sample With Errors"}
              </button>
              <a
                href={sampleErrorFile}
                download="sample_qc_upload_error.csv"
                style={S.downloadIconLink}
                className="btn-hover tooltip"
                data-tip="Download the error sample file"
              >
                ⭳
              </a>
            </div>

            <div className="qc-upload-action" style={S.uploadActionGroup}>
              <label className="qc-file-label" style={S.fileLabel}>
                <span style={S.fileLabelBtn}>Choose File</span>
                <span style={S.fileLabelName}>{file ? file.name : "No file chosen"}</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={e => setFile(e.target.files[0])}
                  style={S.fileInputHidden}
                />
              </label>
              <button style={S.uploadBtn} onClick={() => handleUpload()} className="btn-hover">
                {loading ? "Validating…" : "Upload & Validate"}
              </button>
            </div>
          </div>
        </div>

        {/* ══ LOADER ══ */}
        {loading && (
          <div style={S.loaderWrap}>
            <div style={S.spinner} />
            <span style={{ fontSize:"13px", color:"#6a7d8c", fontWeight:500, fontFamily:"Poppins, sans-serif" }}>
              Analyzing data quality across{" "}
              <span style={{ color:"#0c3c54", fontWeight:600 }}>product hierarchies</span>,{" "}
              <span style={{ color:"#032B4E", fontWeight:600 }}>geography</span> &amp;{" "}
              <span style={{ color:"#E06639", fontWeight:600 }}>sales metrics</span>…
            </span>
          </div>
        )}

        {/* ══ RESULTS ══ */}
        {result && !loading && (
          <div style={{ animation:"fadeUp .45s ease" }}>

            {/* ─ Data metrics ─ */}
            <MetricsPanel summary={summary} totalRows={result.total_rows} />

            {/* ─ Duplicate warning ─ */}
            {hasDuplicates && (
              <div style={{ ...S.alertBox, borderColor:"#e11d48", background:"#fff1f2" }}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:"14px" }}>
                  <span style={{ fontSize:"26px" }}>⚠</span>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:0, fontSize:"15px", fontWeight:800, color:"#e11d48" }}>
                      {summary.duplicate_rows} duplicate row(s) detected
                    </p>
                    <p style={{ margin:"4px 0 0", fontSize:"13px", color:"#6a7d8c" }}>
                      Download the file below, locate the highlighted duplicates, delete them manually, then re-upload the cleaned file.
                    </p>
                  </div>
                </div>
                <button style={{ ...S.downloadBtn, background:"#dc2626" }} onClick={downloadExcel} className="btn-hover">
                  Download to Remove Duplicates
                </button>
              </div>
            )}

            {/* ─ Issue tabs ─ */}
            {tabTypes.length > 0 && (
              <>
                <div style={S.sectionHead}>
                  <div>
                    <span style={S.sectionTitle}>Issue Breakdown</span>
                    <p style={{ margin:"3px 0 0", fontSize:"12px", color:"#94a3b8" }}>
                      {displayIssueCount} issue{displayIssueCount !== 1 ? "s" : ""} detected across {tabTypes.length} categor{tabTypes.length !== 1 ? "ies" : "y"}
                    </p>
                  </div>
                </div>

                <div style={S.tabBar}>
                  {tabTypes.map(type => {
                    const meta   = ISSUE_META[type] || { label:type, color:"#6a7d8c", bg:"#f4f3ef", icon:"!" };
                const count = type === "MISSING_TIMESERIES"
                  ? summary.missing_timeseries_total ?? issuesByType[type].length
                  : type === "DUPLICATE_ROWS"
                    ? summary.duplicate_rows ?? issuesByType[type].length
                    : issuesByType[type].filter(i => i.row_data).length;
                    const active = activeTab === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setActiveTab(type)}
                        className="btn-hover qc-tab"
                        style={{
                          ...S.tab,
                          cursor: "pointer",
                          background:   active ? "#032B4E" : "#fff",
                          color:        active ? "#fff"      : "#0c3c54",
                          borderColor:  active ? "#032B4E" : "#e6eef4",
                          boxShadow:    active ? "0 3px 10px rgba(3,43,78,.18)" : "none",
                        }}
                      >
                        <span style={{ fontSize:"14px", opacity: active ? 1 : 0.7 }}>{meta.icon}</span>
                        <span>{meta.label}</span>
                        <span style={{
                          background: active ? "rgba(255,255,255,.2)" : meta.bg,
                          color:      active ? "#fff" : meta.color,
                          borderRadius:"8px", padding:"2px 8px", fontSize:"11px", fontWeight:700,
                        }}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* ─ Content per tab ─ */}
                {activeTab === "MISSING_TIMESERIES" ? (
                  <TimeseriesPanel data={summary.missing_timeseries || []} dateMin={summary.date_min} dateMax={summary.date_max} />
                ) : activeTab === "DUPLICATE_ROWS" ? (
                  <div style={S.infoBox}>
                    <span style={{ fontSize:"22px" }}>ℹ️</span>
                    <span>This issue requires manual Excel edits. Download the file above, remove duplicate rows, and re-upload.</span>
                  </div>
                ) : (
                  <IssueTable
                    issues={activeIssues}
                    issueType={activeTab}
                    allIssuesByRow={issuesByRowAll}
                    fixes={fixes}
                    setFixes={setFixes}
                    saved={saved}
                    saving={saving}
                    onSaveBatch={handleSaveRowBatch}
                    onUnsave={handleUnsaveRow}
                    onDownload={downloadExcel}
                  />
                )}
              </>
            )}

            {tabTypes.length === 0 && (
              <div style={S.successBox}>
                <span style={{ fontSize:"32px" }}>✅</span>
                <div>
                  <p style={{ margin:0, fontSize:"16px", fontWeight:800, color:"#059669" }}>All Quality Checks Passed</p>
                  <p style={{ margin:"3px 0 0", fontSize:"13px", color:"#6a7d8c" }}>
                    Your data is clean, validated, and ready for the forecasting pipeline.
                  </p>
                </div>
              </div>
            )}

            {/* ─ Commit bar ─ */}
            <CommitBar 
              isGreen={isGreen} 
              status={isGreen ? "READY" : "ISSUES PENDING"} 
              issueCount={currentFixableCount} 
              committing={committing} 
              onCommit={handleCommit} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   SCORE CARD
══════════════════════════════════════════ */
function ScoreCard({ isGreen, score, remaining }) {
  const ringColor = isGreen ? "#059669" : "#E06639";
  const subLabel = isGreen
    ? "✓ All Clear"
    : `${remaining} issue${remaining !== 1 ? "s" : ""} remaining`;

  return (
    <div style={{
      ...S.scoreCard,
      background: isGreen
        ? "linear-gradient(160deg,#f0fdf4,#dcfce7)"
        : "linear-gradient(160deg,#fff7ed,#fef2f2)",
      border: `1px solid ${isGreen ? "#bbf7d0" : "#fed7aa"}`,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
        <div style={{
          ...S.scoreRing,
          background: `conic-gradient(${ringColor} ${score * 3.6}deg, #e6eef4 0deg)`,
        }}>
          <div style={S.scoreRingInner}>
            <span style={{ fontSize:"15px", fontWeight:800, color: ringColor, lineHeight:1, fontFamily:"Poppins, sans-serif" }}>
              {score}%
            </span>
          </div>
        </div>
        <div style={{ textAlign:"left" }}>
          <p style={{ margin:0, fontSize:"10px", color:"#94a3b8", fontWeight:700, letterSpacing:".08em", fontFamily:"Poppins, sans-serif" }}>
            FIX PROGRESS
          </p>
          <p style={{
            margin:"4px 0 0", fontSize:"12px", fontWeight:800,
            color: isGreen ? "#15803d" : "#c2410c",
            fontFamily:"Poppins, sans-serif", whiteSpace:"nowrap",
          }}>
            {subLabel}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   METRICS PANEL
══════════════════════════════════════════ */
function MetricsPanel({ summary, totalRows }) {
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "Not available";
  
  return (
    <div className="qc-metrics-panel" style={S.metricsPanel}>
      {/* Date range */}
      <div style={S.dateRangeBox}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"8px" }}>
          <span style={{ fontSize:"22px" }}>📅</span>
          <span style={{ fontSize:"14px", fontWeight:800, color:"#032B4E" }}>Data Availability Range</span>
        </div>
        <p style={{ margin:0, fontSize:"13px", color:"#6a7d8c", lineHeight:1.6 }}>
          Forecast data spans from <strong style={{ color:"#10b981" }}>{fmt(summary.date_min)}</strong> to <strong style={{ color:"#10b981" }}>{fmt(summary.date_max)}</strong>
        </p>
      </div>

      {/* Stats grid */}
      <div className="qc-stats-grid" style={S.statsGrid}>
        <Metric label="Total Rows"       value={summary.total_no_of_rows ?? totalRows} icon="📊" />
        <Metric label="Average Sales"    value={summary.AVG_value ? summary.AVG_value.toFixed(1) : "—"} icon="📈" />
        <Metric label="Max Sales"        value={summary.max_value ?? "—"} icon="🔼" />
        <Metric label="Min Sales"        value={summary.min_value ?? "—"} icon="🔽" />
        <Metric label="Mean"             value={summary.mean_value ? summary.mean_value.toFixed(1) : "—"} icon="➗" />
        <Metric label="Std Deviation"    value={summary.standard_deviation ? summary.standard_deviation.toFixed(1) : "—"} icon="📏" />
        <Metric label="Duplicates"       value={summary.duplicate_rows ?? 0} accent={summary.duplicate_rows > 0} icon="⚠️" />
        <Metric label="Missing TS"       value={summary.missing_timeseries_total ?? 0} accent={summary.missing_timeseries_total > 0} icon="◷" />
      </div>
    </div>
  );
}

function Metric({ label, value, icon, accent }) {
  return (
    <div className="qc-metric-card" style={S.metricCard}>
      <div style={{
        ...S.metricIconBadge,
        background: accent ? "#fee2e2" : "#eff6ff",
      }}>
        <span style={{ fontSize:"16px" }}>{icon}</span>
      </div>
      <div style={{ minWidth:0 }}>
        <span style={S.metricLabel}>{label}</span>
        <span style={{ ...S.metricValue, color: accent ? "#dc2626" : "#032B4E" }}>{value}</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   TIMESERIES PANEL
══════════════════════════════════════════ */
function TimeseriesPanel({ data, dateMin, dateMax }) {
  const [expanded, setExpanded] = useState({});

  return (
    <div style={{ marginBottom:"16px" }}>
      <div style={S.infoBox}>
        <span style={{ fontSize:"22px" }}>ℹ️</span>
        <div style={{ flex:1 }}>
          <p style={{ margin:0, fontSize:"14px", fontWeight:700, color:"#032B4E" }}>
            Missing Time-series Data Detected
          </p>
          <p style={{ margin:"4px 0 0", fontSize:"12px", color:"#6a7d8c" }}>
            {data.length} SKU(s) have incomplete date ranges. This may impact forecast accuracy but is treated as a warning. You can proceed or fix manually in Excel.
          </p>
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:"10px", marginTop:"12px" }}>
        {data.map((entry, i) => {
          const open = expanded[i];
          const dates = entry.missing_dates || [];
          // "gaps" = contiguous missing-period runs (e.g. 3 back-to-back missing
          // months = 1 gap). Falls back to the raw count for older API responses.
          const gapCount = typeof entry.gaps === "number" ? entry.gaps : dates.length;
          return (
            <div key={i} style={{ background:"#fff", border:"2px solid #e0f2fe", borderRadius:"12px", overflow:"hidden" }}>
              <div
                onClick={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                style={{
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"14px 20px", cursor:"pointer", background: open ? "#f0f9ff" : "#fff",
                  transition:"background .2s",
                }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                  <span style={{ fontSize:"20px" }}>◷</span>
                  <div>
                    <p style={{ margin:0, fontSize:"13px", fontWeight:800, color:"#032B4E" }}>{entry.sku}</p>
                    <p style={{ margin:"2px 0 0", fontSize:"11px", color:"#94a3b8" }}>
                      {dates.length} missing date{dates.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                  <span style={{
                    background:"#dbeafe", color:"#032B4E", borderRadius:"20px",
                    padding:"3px 12px", fontSize:"11px", fontWeight:800, border:"1px solid #bfdbfe",
                  }}>
                    {gapCount} gap{gapCount !== 1 ? "s" : ""}
                  </span>
                  <span style={{
                    color:"#94a3b8", fontSize:"16px", transition:"transform .2s",
                    transform: open ? "rotate(180deg)" : "rotate(0)",
                  }}>
                    ▾
                  </span>
                </div>
              </div>

              {open && (
                <div style={{ padding:"16px 20px", borderTop:"2px solid #e0f2fe", background:"#f0f9ff" }}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                    {dates.map(d => (
                      <span key={d} style={{
                        background:"#fff", border:"1px solid #bae6fd",
                        borderRadius:"6px", padding:"4px 10px", fontSize:"11px", fontWeight:700, color:"#032B4E",
                      }}>
                        {new Date(d).toLocaleDateString("en-IN", { month:"short", year:"2-digit" })}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   ISSUE TABLE (COMPACT) — GROUPED BY ROW
══════════════════════════════════════════ */
function IssueTable({ issues, issueType, allIssuesByRow, fixes, setFixes, saved, saving, onSaveBatch, onUnsave, onDownload }) {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Group issues by row number (moved before early return)
  const issuesByRow = useMemo(() => {
    const map = {};
    issues.forEach(iss => {
      if (!map[iss.row]) map[iss.row] = [];
      map[iss.row].push(iss);
    });
    return map;
  }, [issues]);

  // Get all columns from all issues (moved before early return)
  const allColumns = useMemo(() => {
    const cols = new Set();
    issues.forEach(iss => {
      Object.keys(iss.row_data || {}).forEach(col => cols.add(col));
    });
    return Array.from(cols);
  }, [issues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [issueType]);

  if (!issues.length) return <div style={S.emptyState}>No fixable rows for this issue type.</div>;

  const meta = ISSUE_META[issueType] || { color:"#6a7d8c" };

  const uniqueRowNums = Object.keys(issuesByRow)
    .map(r => parseInt(r, 10))
    .sort((a, b) => a - b);

  const totalPages = Math.ceil(uniqueRowNums.length / ITEMS_PER_PAGE);
  const paginatedRowNums = uniqueRowNums.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleChange = (rowNum, col, val) => {
    const rowKey = `${rowNum}-${col}`;
    setFixes(prev => ({ ...prev, [rowKey]: { ...(prev[rowKey] || {}), [col]: val } }));
  };

  return (
    <div>
      {issues.length > 10 && (
        <div style={{ ...S.alertBox, marginBottom: "16px", background: "#fff", borderColor: meta.color }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:"14px" }}>
            <span style={{ fontSize:"26px", lineHeight:1 }}>⚠️</span>
            <div style={{ flex:1 }}>
              <p style={{ margin:0, fontSize:"15px", fontWeight:800, color: meta.color }}>
                {issues.length} {meta.label} issues detected across {uniqueRowNums.length} row{uniqueRowNums.length !== 1 ? "s" : ""}
              </p>
              <p style={{ margin:"4px 0 0", fontSize:"13px", color:"#6a7d8c", lineHeight:1.5 }}>
                For bulk issues, download the Excel file, fix offline, and re-upload to save time. Or fix inline below.
              </p>
            </div>
          </div>
          <button style={{ ...S.downloadBtn, background: meta.color }} onClick={onDownload} className="btn-hover">
            📥 Download Excel
          </button>
        </div>
      )}

      <div style={{ borderRadius:"14px", overflow:"hidden", border:`2px solid ${meta.color}`, marginBottom:"16px", background:"#fff" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"linear-gradient(135deg,#032B4E,#032B4E)" }}>
                <th style={{ ...S.thCompact, minWidth:"50px" }}>Row</th>
                {allColumns.map(col => (
                  <th key={col} style={{ ...S.thCompact, minWidth: col === "sales" ? "90px" : "110px" }}>
                    {col.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
                <th style={{ ...S.thCompact, minWidth:"80px" }}>Issues</th>
                <th style={{ ...S.thCompact, background:meta.color, minWidth:"100px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRowNums.map((rowNum) => {
                const rowIssues = issuesByRow[rowNum];
                const firstIssue = rowIssues[0];
                // Columns editable in this view: this tab's own issue column(s)
                // plus any other column flagged under a different tab for the
                // same physical row (e.g. a null Range while viewing Fractional Sales).
                const fullRowIssues = allIssuesByRow?.[rowNum] || rowIssues;
                const bonusIssues = fullRowIssues.filter(
                  fi => !rowIssues.some(oi => oi.col === fi.col && oi.type === fi.type)
                );
                const issueColsSet = new Set(fullRowIssues.map(i => i.col));
                const severities = [...new Set(rowIssues.map(i => i.severity))];
                const sevMeta = SEVERITY_META[severities[0]] || SEVERITY_META.INFO;
                const messages = rowIssues.map(i => i.message || i.msg || i.details || "—").join(" • ");

                // Row counts as "done" once this tab's own issue column(s) are
                // saved — a bonus column from another tab doesn't block it.
                const allSaved = rowIssues.every(iss => saved[`${rowNum}-${iss.col}`]);

                return (
                  <tr key={`row-${rowNum}`} style={{
                    borderTop:"1px solid #f4f3ef",
                    background: allSaved ? "#f0fdf4" : "#fef2f2",
                    transition:"background .25s",
                  }}>
                    <td style={{ ...S.tdCompact, fontWeight:800, color:"#dc2626" }}>
                      {rowNum}
                    </td>

                    {allColumns.map(col => {
                      const hasIssue = issueColsSet.has(col);
                      const rowKey = `${rowNum}-${col}`;
                      const rowFixes = fixes[rowKey] || {};
                      const isSaved = saved[rowKey];
                      const rawVal = firstIssue.row_data[col] ?? null;
                      const curVal = rowFixes[col] !== undefined ? rowFixes[col] : (rawVal === null ? "" : rawVal ?? "");

                      // Show editable input if this column has an issue and not saved
                      if (hasIssue && !isSaved) {
                        return (
                          <td key={col} style={{
                            ...S.tdCompact,
                            background:`${meta.color}18`,
                            padding:"6px 8px",
                          }}>
                            <input
                              className="row-fix-input"
                              type={col === "sales" ? "number" : "text"}
                              value={curVal}
                              onChange={e => handleChange(rowNum, col, e.target.value)}
                              style={{
                                ...S.compactInput,
                                border:`2px solid ${meta.color}`,
                                width:"100%",
                                maxWidth:"none",
                                backgroundColor: rawVal === null ? "#fff3f0" : "#fff",
                              }}
                              placeholder={rawVal === null ? "Enter value (null)" : "Fix…"}
                            />
                          </td>
                        );
                      }

                      // Display-only cell
                      return (
                        <td key={col} style={{
                          ...S.tdCompact,
                          fontWeight: hasIssue ? 700 : 400,
                          color: rawVal === null ? "#e11d48" : "#0c3c54",
                          background: hasIssue && isSaved ? "#f0fdf4" : "inherit",
                        }}>
                          <div style={{
                            maxWidth:"120px",
                            overflow:"hidden",
                            textOverflow:"ellipsis",
                            whiteSpace:"nowrap",
                          }}>
                            {rawVal === null || rawVal === undefined ? (
                              <span style={{
                                fontSize:"11px",
                                fontStyle:"italic",
                                fontWeight:800,
                                color:"#e11d48",
                                background:"#fef2f2",
                                padding:"2px 6px",
                                borderRadius:"4px",
                                display:"inline-block",
                              }}>
                                NULL
                              </span>
                            ) : (
                              String(rawVal)
                            )}
                          </div>
                        </td>
                      );
                    })}

                    <td style={{ ...S.tdCompact, minWidth:"80px" }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
                        {severities.map(sev => {
                          const sm = SEVERITY_META[sev] || SEVERITY_META.INFO;
                          return (
                            <span
                              key={sev}
                              style={{
                                ...S.badgeCompact,
                                background:sm.bg,
                                color:sm.color,
                                fontSize:"9px",
                              }}
                            >
                              {sev}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    <td style={{
                      ...S.tdCompact,
                      background:`${meta.color}0d`,
                      minWidth:"100px",
                    }}>
                      {allSaved ? (
                        <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
                          <span style={S.savedChip}>✓ Saved</span>
                          <button
                            onClick={() => onUnsave(rowNum, rowIssues)}
                            style={{
                              ...S.actionBtn,
                              background:"#6a7d8c",
                              fontSize:"10px",
                              padding:"4px 8px",
                              minHeight:"unset",
                            }}
                          >
                            ✎ Re-edit
                          </button>
                        </div>
                      ) : (
                        <button
                          disabled={saving === rowNum}
                          onClick={() => onSaveBatch(rowNum, rowIssues, bonusIssues)}
                          className="btn-hover"
                          style={{
                            ...S.actionBtn,
                            background:meta.color,
                            opacity: saving === rowNum ? 0.6 : 1,
                          }}
                        >
                          {saving === rowNum ? "⏳" : "Save"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{
            display:"flex",
            justifyContent:"space-between",
            alignItems:"center",
            padding:"12px 16px",
            borderTop:"1px solid #e2e8f0",
            background:"#f4f3ef",
          }}>
            <span style={{ fontSize:"12px", color:"#6a7d8c", fontWeight:600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display:"flex", gap:"8px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   COMMIT BAR
══════════════════════════════════════════ */
function CommitBar({ isGreen, status, issueCount, committing, onCommit }) {
  return (
    <div style={{
      ...S.commitBar,
      background: isGreen
        ? "linear-gradient(135deg,#f0fdf4,#dcfce7)"
        : "linear-gradient(135deg,#fff7ed,#fef2f2)",
      border: `1px solid ${isGreen ? "#bbf7d0" : "#fed7aa"}`,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:"20px", flex:1 }}>
        <div style={{
          ...S.commitBarIcon,
          background: isGreen ? "#dcfce7" : "#ffedd5",
          color: isGreen ? "#059669" : "#E06639",
        }}>
          {isGreen ? "✓" : "◷"}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"6px" }}>
            <div>
              <p style={{ margin:0, fontSize:"11px", color:"#6a7d8c", fontWeight:700, letterSpacing:".08em", fontFamily:"Poppins, sans-serif" }}>
                QUALITY CONTROL STATUS
              </p>
              <p style={{ margin:"4px 0 0", fontSize:"20px", fontWeight:800, color:"#0c3c54", lineHeight:1.2, fontFamily:"Poppins, sans-serif" }}>
                {status}
              </p>
            </div>
          </div>
          <p style={{ margin:0, fontSize:"13px", color:"#6a7d8c", lineHeight:1.5, fontFamily:"Poppins, sans-serif" }}>
            {isGreen
              ? "All validations passed. Data is clean and ready for the forecasting pipeline."
              : `${issueCount} issue${issueCount !== 1 ? "s" : ""} remaining. Review and fix errors before committing to database.`
            }
          </p>
        </div>
      </div>
      <button
        onClick={onCommit}
        disabled={committing}
        className="btn-hover"
        style={{
          ...S.finalBtn,
          background: isGreen ? "#E06639" : "#032B4E",
          color: "#ffffff",
          cursor: committing ? "not-allowed" : "pointer",
          opacity: committing ? 0.7 : 1,
          boxShadow: isGreen
            ? "0 3px 10px rgba(224, 102, 57, 0.25)"
            : "0 3px 10px rgba(3, 43, 78, 0.2)",
        }}
      >
        {committing ? "Committing…" : (isGreen ? "Finalize & Store Data" : `Finalize with ${issueCount} Issues`)}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════
   STYLES
══════════════════════════════════════════ */
const S = {
  font: { fontFamily: "Poppins, sans-serif" },
  page: {
    minHeight: 0,
    background: "transparent",
    fontFamily: "Poppins, sans-serif",
    padding: 0,
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px 26px",
    boxShadow: "0 4px 20px rgba(3, 43, 78, 0.06)",
    border: "1px solid #e6eef4",
    maxWidth: "100%",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    margin: "-24px -26px 22px",
    padding: "26px 28px",
    borderRadius: "16px 16px 0 0",
    background: "linear-gradient(120deg,#032B4E 0%,#0c3c54 55%,#0e5a68 100%)",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.06)",
    flexWrap: "wrap",
  },
  headerBadge: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "rgba(255,255,255,.12)",
    border: "1px solid rgba(255,255,255,.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    flexShrink: 0,
  },
  eyebrow: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: ".14em",
    color: "#7dd3d8",
    textTransform: "uppercase",
    marginBottom: "4px",
    fontFamily: "Poppins, sans-serif",
  },
  title: {
    fontSize: "21px",
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
    letterSpacing: "-0.3px",
    lineHeight: 1.2,
    fontFamily: "Poppins, sans-serif",
  },
  subtitle: {
    fontSize: "12px",
    color: "rgba(255,255,255,.72)",
    marginTop: "4px",
    maxWidth: "520px",
    lineHeight: 1.45,
    fontFamily: "Poppins, sans-serif",
  },
  scoreCard: {
    borderRadius: "14px",
    padding: "12px 18px",
    boxShadow: "0 2px 10px rgba(0,0,0,.06)",
    minWidth: "190px",
    flexShrink: 0,
  },
  scoreRing: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.5s ease",
  },
  scoreRingInner: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadZone: {
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(135deg,#f8fafc,#f0f9ff)",
    border: "1.5px dashed #bcd4e6",
    borderRadius: "14px",
    padding: "18px 20px",
    gap: "16px",
    marginBottom: "10px",
  },
  uploadLeft: { display: "flex", alignItems: "center", gap: "10px", minWidth: 0 },
  uploadIcon: { fontSize: "22px", lineHeight: 1, flexShrink: 0 },
  uploadTitle: {
    margin: 0,
    fontSize: "13px",
    fontWeight: 700,
    color: "#032B4E",
    fontFamily: "Poppins, sans-serif",
  },
  uploadSub: {
    margin: "2px 0 0",
    fontSize: "11px",
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
  },
  uploadToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    paddingTop: "14px",
    borderTop: "1px solid rgba(3,43,78,.08)",
  },
  uploadSampleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  uploadActionGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  fileLabel: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "4px 4px 4px 4px",
    cursor: "pointer",
    minWidth: 0,
  },
  fileLabelBtn: {
    background: "#0c3c54",
    color: "#fff",
    borderRadius: "6px",
    padding: "7px 14px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    fontFamily: "Poppins, sans-serif",
    flexShrink: 0,
  },
  fileLabelName: {
    fontSize: "12px",
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "160px",
    paddingRight: "8px",
  },
  fileInputHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    border: 0,
  },
  secondaryBtn: {
    background: "#ecfdf5",
    color: "#032B4E",
    border: "1px solid #a7f3d0",
    borderRadius: "8px",
    padding: "7px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    fontFamily: "Poppins, sans-serif",
    display: "inline-flex",
    alignItems: "center",
  },
  sampleErrorBtn: {
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fed7aa",
    borderRadius: "8px",
    padding: "7px 12px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "Poppins, sans-serif",
    display: "inline-flex",
    alignItems: "center",
  },
  downloadIconLink: {
    background: "#fff",
    color: "#c2410c",
    border: "1px solid #fed7aa",
    borderRadius: "8px",
    width: "28px",
    height: "28px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  uploadBtn: {
    background: "#059669",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: ".01em",
    whiteSpace: "nowrap",
    boxShadow: "none",
    transition: "background .15s",
    fontFamily: "Poppins, sans-serif",
  },
  loaderWrap: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 0" },
  spinner: {
    width: "20px",
    height: "20px",
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #059669",
    borderRadius: "50%",
    animation: "spin .7s linear infinite",
    flexShrink: 0,
  },
  metricsPanel: {
    background:"linear-gradient(135deg,#f0f9ff,#e0f2fe)",
    border:"2px solid #bae6fd", borderRadius:"16px",
    padding:"24px 28px", margin:"24px 0 20px",
  },
  dateRangeBox: {
    marginBottom:"20px",
  },
  statsGrid: {
    display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",
    gap:"12px",
  },
  metricCard: {
    display:"flex", alignItems:"center", gap:"12px",
    background:"#fff", border:"1px solid #e2e8f0",
    borderRadius:"12px", padding:"14px 16px",
  },
  metricIconBadge: {
    width:"36px", height:"36px", borderRadius:"10px", flexShrink:0,
    display:"flex", alignItems:"center", justifyContent:"center",
  },
  metricLabel: {
    fontSize:"10px", fontWeight:700, color:"#6a7d8c",
    letterSpacing:".04em", textTransform:"uppercase", display:"block",
  },
  metricValue: {
    fontSize:"19px", fontWeight:900, color:"#032B4E", display:"block", marginTop:"3px",
    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
  },
  alertBox: {
    display:"flex", alignItems:"flex-start", justifyContent:"space-between",
    background:"#fef2f2", border:"2px solid #fca5a5", borderRadius:"16px",
    padding:"20px 28px", margin:"0 0 20px", gap:"20px", flexWrap:"wrap",
  },
  downloadBtn: {
    background:"#dc2626", color:"#fff", border:"none", borderRadius:"12px",
    padding:"11px 24px", fontSize:"13px", fontWeight:800,
    cursor:"pointer", whiteSpace:"nowrap", transition:"all .2s",
    boxShadow:"0 4px 14px rgba(220,38,38,.3)",
  },
  infoBox: {
    display:"flex", alignItems:"center", gap:"14px",
    background:"#eff6ff", border:"2px solid #bfdbfe", borderRadius:"12px",
    padding:"16px 24px", fontSize:"13px", color:"#032B4E",
    fontWeight:600, marginBottom:"16px",
  },
  successBox: {
    display:"flex", alignItems:"center", gap:"16px",
    background:"linear-gradient(135deg,#f0fdf4,#dcfce7)",
    border:"2px solid #86efac", borderRadius:"16px",
    padding:"20px 28px", margin:"16px 0 24px",
  },
  sectionHead: {
    display:"flex", alignItems:"flex-start", justifyContent:"space-between",
    margin:"28px 0 16px", gap:"16px",
  },
  sectionTitle: {
    fontSize:"18px", fontWeight:900, color:"#032B4E", letterSpacing:"-0.3px",
  },
  tabBar: {
    display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"20px",
  },
  tab: {
    display:"flex", alignItems:"center", gap:"8px",
    padding:"10px 20px", borderRadius:"12px", fontSize:"13px",
    fontWeight:800, cursor:"pointer", border:"2px solid",
    transition:"all .25s", letterSpacing:".02em",
  },
  thCompact: {
    padding:"10px 12px", textAlign:"left", fontSize:"10px",
    fontWeight:900, color:"#fff", background:"#032B4E",
    letterSpacing:".08em", textTransform:"uppercase", whiteSpace:"nowrap",
  },
  tdCompact: {
    padding:"8px 12px", fontSize:"12px", color:"#0c3c54",
    verticalAlign:"middle",
  },
  compactInput: {
    borderRadius:"8px", padding:"5px 10px", fontSize:"12px",
    width:"100%", maxWidth:"110px", fontWeight:700, background:"#fff",
    transition:"all .15s",
  },
  badgeCompact: {
    borderRadius:"6px", padding:"3px 9px", fontSize:"10px",
    fontWeight:800, letterSpacing:".04em", whiteSpace:"nowrap",
  },
  actionBtn: {
    color:"#fff", border:"none", borderRadius:"8px",
    padding:"6px 16px", fontSize:"11px", fontWeight:800,
    cursor:"pointer", whiteSpace:"nowrap", letterSpacing:".02em",
    transition:"all .2s", width:"100%",
  },
  pageBtn: {
    padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1",
    background: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 700,
    color: "#0c3c54", transition: "all 0.2s"
  },
  savedChip: {
    background:"#dcfce7", color:"#15803d", borderRadius:"8px",
    padding:"6px 14px", fontSize:"11px", fontWeight:800, display:"block", textAlign:"center",
  },
  commitBar: {
    marginTop:"32px", borderRadius:"18px",
    padding:"24px 32px", display:"flex", alignItems:"center",
    gap:"28px", flexWrap:"wrap",
    boxShadow:"0 8px 24px rgba(3,43,78,.08)",
  },
  commitBarIcon: {
    width:"48px", height:"48px", borderRadius:"14px", flexShrink:0,
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:"22px", fontWeight:800,
  },
  finalBtn: {
    padding:"16px 40px", borderRadius:"12px", border:"none",
    fontWeight:900, fontSize:"15px", letterSpacing:".02em",
    transition:"all .2s", whiteSpace:"nowrap",
  },
  emptyState: {
    textAlign:"center", padding:"40px 0", color:"#94a3b8",
    fontSize:"14px", fontStyle:"italic",
  },
};